import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import TrialBalanceView from "@/pages/TrialBalanceView";
import DayBookView from "@/pages/DayBookView";
import InventoryBooksView from "@/pages/InventoryBooksView";
import AccountBooksView from "@/pages/reports/AccountBooksView";
import StatutoryReportsView from "@/pages/reports/StatutoryReportsView";
import { StatementsAccountsView, StatementsInventoryView } from "@/pages/reports/StatementsViews";
import { CashFlowView, ReceiptsPaymentsView, ListOfAccountsView, ExceptionReportsView } from "@/pages/reports/MiscReports";
import { BalanceSheetView, ProfitLossView, StockSummaryView, BankBookView, AuditReportView, OpeningBalancesView } from "@/pages/TallyFinance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIN_VIEWS = {
  "balance-sheet": BalanceSheetView,
  "profit-loss": ProfitLossView,
  "stock-summary": StockSummaryView,
  "bank-book": BankBookView,
  "audit-report": AuditReportView,
  "opening-balances": OpeningBalancesView,
};
const FIN_ITEMS = [
  { key: "balance-sheet", name: "Balance Sheet" },
  { key: "profit-loss", name: "Profit & Loss A/c" },
  { key: "stock-summary", name: "Stock Summary" },
  { key: "bank-book", name: "Bank Statement" },
  { key: "audit-report", name: "Audit Report" },
  { key: "opening-balances", name: "Opening Balances (Books Beginning)" },
];

const VIEW_COMPONENTS = {
  "trial-balance": TrialBalanceView,
  "day-book": DayBookView,
  "inventory-books": InventoryBooksView,
  "account-books": AccountBooksView,
  "statements-accounts": StatementsAccountsView,
  "statements-inventory": StatementsInventoryView,
  "statutory-reports": StatutoryReportsView,
  "cash-funds-flow": CashFlowView,
  "receipts-payments": ReceiptsPaymentsView,
  "list-of-accounts": ListOfAccountsView,
  "exception-reports": ExceptionReportsView,
};

const REPORT_MENU = [
  { key: "trial-balance", label: "Trial Balance" },
  { key: "day-book", label: "Day Book" },
  { key: "account-books", label: "Account Books" },
  { key: "statements-accounts", label: "Statements of Accounts" },
  { key: "inventory-books", label: "Inventory Books" },
  { key: "statements-inventory", label: "Statements of Inventory" },
  { key: "statutory-reports", label: "Statutory Reports" },
  { key: "cash-funds-flow", label: "Cash/Funds Flow" },
  { key: "receipts-payments", label: "Receipts and Payments" },
  { key: "list-of-accounts", label: "List of Accounts" },
  { key: "exception-reports", label: "Exception Reports" },
];

const fmt = (n) => (n || n === 0) && Math.abs(n) > 0.004
  ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : "";
const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dshow = (d) => {
  if (!d) return "";
  const [y, m, dd] = d.split("-");
  const M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${dd}-${M[(+m || 1) - 1]}-${y.slice(2)}`;
};

function fyDefault() {
  try {
    const saved = JSON.parse(sessionStorage.getItem("tally_period") || "null");
    if (saved && saved.from && saved.to) return saved;
  } catch { /* noop */ }
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: `${y}-04-01`, to: `${y + 1}-03-31` };
}

/* ---------- small UI atoms ---------- */
const Chip = ({ k, label }) => (
  <span className="inline-flex items-center gap-1 mr-3 whitespace-nowrap">
    <kbd className="px-1.5 py-0.5 rounded-sm bg-[#0f766e] text-white text-[10px] font-bold tracking-wide">{k}</kbd>
    <span className="text-[11px] text-slate-600">{label}</span>
  </span>
);

const KeyBar = ({ chips }) => (
  <div className="border-t border-[#e7dcc3] bg-[#f6efdd] px-4 py-2 flex flex-wrap items-center" data-testid="tl-keybar">
    {chips.map(([k, l]) => <Chip key={k + l} k={k} label={l} />)}
  </div>
);

const FnBtn = ({ k, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[12px] font-semibold text-slate-700 hover:bg-[#0f766e] hover:text-white rounded-sm transition-colors">
    <span className="min-w-[42px] inline-block px-1 py-0.5 text-center rounded-sm bg-white/80 border border-[#d8ccae] text-[10px] font-bold text-[#0f766e]">{k}</span>
    {label}
  </button>
);

/* ---------- Period (F2) dialog ---------- */
function PeriodDialog({ period, onApply, onClose }) {
  const [f, setF] = useState(period.from);
  const [t, setT] = useState(period.to);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
      if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); onApply({ from: f, to: t }); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [f, t, onApply, onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" data-testid="tl-period-dialog">
      <div className="bg-[#fbf7ee] border-2 border-[#0f766e] shadow-2xl w-80">
        <div className="bg-[#0f766e] text-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase">Change Period (F2)</div>
        <div className="p-4 space-y-3">
          <label className="block text-xs font-semibold text-slate-600">From
            <input ref={ref} type="date" value={f} onChange={(e) => setF(e.target.value)} data-testid="tl-period-from"
              className="mt-1 w-full border border-[#d8ccae] rounded-sm px-2 py-1.5 text-sm bg-white" />
          </label>
          <label className="block text-xs font-semibold text-slate-600">To
            <input type="date" value={t} onChange={(e) => setT(e.target.value)} data-testid="tl-period-to"
              className="mt-1 w-full border border-[#d8ccae] rounded-sm px-2 py-1.5 text-sm bg-white" />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-3 py-1 text-xs font-bold border border-[#d8ccae] rounded-sm">Esc Cancel</button>
            <button onClick={() => onApply({ from: f, to: t })} data-testid="tl-period-apply"
              className="px-3 py-1 text-xs font-bold bg-[#0f766e] text-white rounded-sm">Enter Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Voucher create/edit dialog ---------- */
function VoucherDialog({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({
    vch_type: initial.vch_type || "receipt",
    date: initial.date || new Date().toISOString().slice(0, 10),
    party_name: initial.party_name || "",
    account: initial.account || "Cash",
    amount: initial.amount || "",
    invoice_no: initial.invoice_no || "",
    reference: initial.reference || "",
    narration: initial.narration || "",
  });
  const [bills, setBills] = useState([]);
  const [busy, setBusy] = useState(false);
  const firstRef = useRef(null);
  const partyRef = useRef(null);
  useEffect(() => {
    if (!f.party_name.trim()) partyRef.current?.focus();
    else firstRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const p = f.party_name.trim();
    if (!p) { setBills([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/voucher-party-bills`, { params: { vch_type: f.vch_type, party: p } })
        .then((r) => setBills(r.data.bills || [])).catch(() => setBills([]));
    }, 300);
    return () => clearTimeout(t);
  }, [f.party_name, f.vch_type]);

  const save = useCallback(async () => {
    if (!f.party_name.trim()) { toast.error("Enter the party name"); return; }
    if (!f.amount || Number(f.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const body = { ...f, amount: Number(f.amount) };
      if (isEdit) await axios.put(`${API}/vouchers/${initial.id}`, body);
      else await axios.post(`${API}/vouchers`, body);
      toast.success(isEdit ? "Voucher updated" : "Voucher saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the voucher");
    } finally { setBusy(false); }
  }, [f, isEdit, initial.id, onSaved]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
      if (e.key === "Enter" && (e.ctrlKey || e.altKey)) { e.preventDefault(); e.stopPropagation(); save(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, save]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const isRcp = f.vch_type === "receipt";
  const inp = "mt-1 w-full border border-[#d8ccae] rounded-sm px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0f766e]";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" data-testid="tl-voucher-dialog">
      <div className="bg-[#fbf7ee] border-2 border-[#0f766e] shadow-2xl w-full max-w-md">
        <div className={`text-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase ${isRcp ? "bg-[#0f766e]" : "bg-[#9a3412]"}`}>
          {isEdit ? "Alter" : "Create"} {isRcp ? "Receipt" : "Payment"} Voucher {isEdit ? `— ${initial.vch_no || ""}` : ""}
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-slate-600">Date
            <input ref={firstRef} type="date" value={f.date} onChange={set("date")} data-testid="tl-vch-date" className={inp} />
          </label>
          <label className="text-xs font-semibold text-slate-600">Mode
            <select value={f.account} onChange={set("account")} data-testid="tl-vch-account" className={inp}>
              <option>Cash</option><option>Bank</option><option>Cheque</option>
            </select>
          </label>
          <label className="col-span-2 text-xs font-semibold text-slate-600">Party
            <input ref={partyRef} value={f.party_name} onChange={set("party_name")} data-testid="tl-vch-party" className={inp} placeholder="Party name (required)" />
          </label>
          <label className="text-xs font-semibold text-slate-600">Amount
            <input type="number" min="0" step="0.01" value={f.amount} onChange={set("amount")} data-testid="tl-vch-amount" className={inp} placeholder="0.00" />
          </label>
          <label className="text-xs font-semibold text-slate-600">Against Bill
            <select value={f.invoice_no} onChange={set("invoice_no")} data-testid="tl-vch-bill" className={inp}>
              <option value="">On Account</option>
              {bills.map((b) => <option key={b.no} value={b.no}>{b.no} · {dshow(b.date)} · {fmt0(b.total)}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold text-slate-600">Reference
            <input value={f.reference} onChange={set("reference")} data-testid="tl-vch-ref" className={inp} placeholder="Cheque / UTR no." />
          </label>
          <label className="text-xs font-semibold text-slate-600">Narration
            <input value={f.narration} onChange={set("narration")} data-testid="tl-vch-narration" className={inp} placeholder="Notes" />
          </label>
        </div>
        <div className="px-4 pb-4 flex justify-between items-center">
          <span className="text-[11px] text-slate-500">Ctrl+Enter = Save · Esc = Cancel</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold border border-[#d8ccae] rounded-sm">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="tl-vch-save"
              className="px-4 py-1.5 text-xs font-bold bg-[#0f766e] text-white rounded-sm disabled:opacity-50">
              {busy ? "Saving…" : "Accept (Ctrl+Enter)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Ledger statement (Tally ledger vouchers view) ---------- */
function StatementView({ ledger, period, onBack, onOpenPeriod, onOpenReports, blocked }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(0);
  const [billsOpen, setBillsOpen] = useState(false);
  const [vform, setVform] = useState(null);
  const rowRefs = useRef({});

  const load = useCallback(() => {
    axios.get(`${API}/tally/ledger-statement`, {
      params: { category: ledger.cat, name: ledger.name, lid: ledger.id || "", frm: period.from, to: period.to },
    }).then((r) => { setData(r.data); setSel(0); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load the ledger"));
  }, [ledger, period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const rows = data?.rows || [];

  const openRow = useCallback((r) => {
    if (!r) return;
    // Open the entry FULLY in alteration mode (Tally-style)
    if (r.src === "voucher" && r.voucher) setVform(r.voucher);
    else if (r.src === "invoice") navigate(`/invoices/${r.id}/edit`);
    else if (r.src === "payment" && r.invoice_id) navigate(`/invoices/${r.invoice_id}`);
    else if (r.src === "purchase") navigate(`/purchases?edit=${r.id}`);
    else if (r.src === "note") navigate(ledger.cat === "creditors" ? `/purchase-notes/${r.id}/edit` : `/notes/${r.id}/edit`);
    else if (r.src === "bank") navigate("/bank");
  }, [navigate, ledger.cat]);

  const delRow = useCallback(async (r) => {
    if (!r) return;
    if (r.src === "voucher") {
      if (!window.confirm(`Delete voucher ${r.vch_no} (${fmt0(r.debit || r.credit)})?`)) return;
      try { await axios.delete(`${API}/vouchers/${r.id}`); toast.success("Voucher deleted"); load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
    } else if (r.src === "payment" && r.invoice_id) {
      if (!window.confirm(`Delete this receipt of ${fmt0(r.credit || r.debit)}?`)) return;
      try { await axios.delete(`${API}/invoices/${r.invoice_id}/payments/${r.id}`); toast.success("Receipt deleted"); load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
    } else {
      toast.info("Sales/Purchase bills are deleted from their own module — press Enter to open");
    }
  }, [load]);

  const doExport = useCallback(async (format) => {
    try {
      const res = await axios.get(`${API}/tally/ledger-statement/export`, {
        params: { category: ledger.cat, name: ledger.name, lid: ledger.id || "", frm: period.from, to: period.to, format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(ledger.name || ledger.cat).replace(/[^\w.-]+/g, "_")}_ledger.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(format === "pdf" ? "PDF exported" : "Excel exported");
    } catch { toast.error("Export failed"); }
  }, [ledger, period]);

  const newVoucher = useCallback((vch_type) => {
    const party = (ledger.cat === "debtors" || ledger.cat === "creditors") ? ledger.name : "";
    setVform({ vch_type, party_name: party });
  }, [ledger]);

  useEffect(() => {
    if (vform || blocked) return undefined;
    const onKey = (e) => {
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      if (e.key === "ArrowDown") { stop(); setSel((s) => Math.min(s + 1, rows.length - 1)); }
      else if (e.key === "ArrowUp") { stop(); setSel((s) => Math.max(s - 1, 0)); }
      else if (e.key === "PageDown") { stop(); setSel((s) => Math.min(s + 12, rows.length - 1)); }
      else if (e.key === "PageUp") { stop(); setSel((s) => Math.max(s - 12, 0)); }
      else if (e.key === "Home") { stop(); setSel(0); }
      else if (e.key === "End") { stop(); setSel(rows.length - 1); }
      else if (e.key === "Enter") { stop(); openRow(rows[sel]); }
      else if (e.key === "Escape") { stop(); if (billsOpen) setBillsOpen(false); else onBack(); }
      else if (e.key === "F2") { stop(); onOpenPeriod(); }
      else if (e.key === "F5") { stop(); newVoucher("payment"); }
      else if (e.key === "F6") { stop(); newVoucher("receipt"); }
      else if (e.key === "F7") { stop(); setBillsOpen((b) => !b); }
      else if (e.key === "F9") { stop(); onOpenReports(); }
      else if (e.altKey && (e.key === "d" || e.key === "D")) { stop(); delRow(rows[sel]); }
      else if (e.altKey && (e.key === "p" || e.key === "P")) { stop(); doExport("pdf"); }
      else if (e.altKey && (e.key === "e" || e.key === "E")) { stop(); doExport("xlsx"); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [rows, sel, billsOpen, vform, blocked, openRow, delRow, doExport, onBack, onOpenPeriod, onOpenReports, newVoucher]);

  useEffect(() => {
    rowRefs.current[sel]?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  const showBills = billsOpen && (data?.bills || []).length > 0;
  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-[#eef1f4]" data-testid="tl-statement">
      <div className="bg-[#0f766e] text-white px-4 py-2 flex items-center justify-between">
        <div>
          <div className="text-sm font-bold tracking-wide" data-testid="tl-stmt-title">Ledger: {ledger.name}</div>
          <div className="text-[11px] text-teal-100">{dshow(period.from)} to {dshow(period.to)}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-teal-100">Closing Balance</div>
          <div className="text-sm font-bold" data-testid="tl-stmt-closing">
            {data ? `${fmt0(data.closing.amount)} ${data.closing.drcr}` : "…"}
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col bg-white border-r border-[#e7dcc3]">
          <div className="flex-1 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[#f6efdd] text-slate-700">
                  <th className="text-left px-3 py-1.5 font-bold w-24">Date</th>
                  <th className="text-left px-3 py-1.5 font-bold">Particulars</th>
                  <th className="text-left px-3 py-1.5 font-bold w-28">Vch Type</th>
                  <th className="text-left px-3 py-1.5 font-bold w-32">Vch No.</th>
                  <th className="text-right px-3 py-1.5 font-bold w-28">Debit</th>
                  <th className="text-right px-3 py-1.5 font-bold w-28">Credit</th>
                  <th className="text-right px-3 py-1.5 font-bold w-32">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#eee5cf] bg-[#fbf7ee]">
                  <td className="px-3 py-1.5" />
                  <td className="px-3 py-1.5 font-bold italic text-slate-600">Opening Balance</td>
                  <td colSpan={4} />
                  <td className="px-3 py-1.5 text-right font-bold" data-testid="tl-stmt-opening">
                    {data ? `${fmt0(data.opening.amount)} ${data.opening.drcr}` : ""}
                  </td>
                </tr>
                {rows.map((r, i) => (
                  <tr key={`${r.src}-${r.id}-${i}`} ref={(el) => { rowRefs.current[i] = el; }}
                    data-testid={`tl-stmt-row-${i}`}
                    onClick={() => setSel(i)} onDoubleClick={() => openRow(r)}
                    className={`border-b border-[#f1ecdd] cursor-pointer ${sel === i ? "bg-[#0f766e] text-white" : "hover:bg-[#f6efdd]"}`}>
                    <td className="px-3 py-1">{dshow(r.date)}</td>
                    <td className="px-3 py-1 truncate max-w-[280px]">
                      {r.particulars}
                      {r.against && (
                        <span className={`ml-1 text-[10px] ${sel === i ? "text-teal-100" : "text-amber-700"}`}>vs {r.against}</span>
                      )}
                    </td>
                    <td className="px-3 py-1">{r.vch_type}</td>
                    <td className="px-3 py-1">{r.vch_no}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmt(r.debit)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmt(r.credit)}</td>
                    <td className="px-3 py-1 text-right tabular-nums font-medium">{fmt0(r.balance)} {r.bal_drcr}</td>
                  </tr>
                ))}
                {data && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No entries in this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="border-t-2 border-[#0f766e] bg-[#fbf7ee] px-3 py-1.5 grid grid-cols-[1fr_112px_112px_128px] text-[13px] font-bold text-slate-800">
            <span>Current Total</span>
            <span className="text-right tabular-nums" data-testid="tl-stmt-total-dr">{data ? fmt0(data.totals.debit) : ""}</span>
            <span className="text-right tabular-nums" data-testid="tl-stmt-total-cr">{data ? fmt0(data.totals.credit) : ""}</span>
            <span className="text-right tabular-nums text-[#0f766e]">{data ? `${fmt0(data.closing.amount)} ${data.closing.drcr}` : ""}</span>
          </div>
        </div>

        {showBills && (
          <div className="w-96 bg-[#fbf7ee] border-l border-[#e7dcc3] overflow-y-auto" data-testid="tl-bills-panel">
            <div className="bg-[#9a3412] text-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase sticky top-0">
              Bill-wise Outstanding (F7)
            </div>
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-[#f6efdd] text-slate-700">
                  <th className="text-left px-2 py-1 font-bold">Bill</th>
                  <th className="text-left px-2 py-1 font-bold">Date</th>
                  <th className="text-right px-2 py-1 font-bold">Amount</th>
                  <th className="text-right px-2 py-1 font-bold">Pending</th>
                </tr>
              </thead>
              <tbody>
                {(data?.bills || []).map((b, i) => (
                  <tr key={b.no + i} data-testid={`tl-bill-${i}`} className="border-b border-[#f1ecdd]">
                    <td className="px-2 py-1 font-medium">{b.no}</td>
                    <td className="px-2 py-1">{dshow(b.date)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmt0(b.total)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums font-bold ${b.outstanding > 0 ? "text-red-700" : "text-emerald-700"}`}>
                      {fmt0(b.outstanding)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#f6efdd] font-bold">
                  <td className="px-2 py-1.5" colSpan={3}>Total Outstanding</td>
                  <td className="px-2 py-1.5 text-right text-red-700" data-testid="tl-bills-total">{fmt0(data?.bills_outstanding)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div className="w-44 bg-[#f6efdd] border-l border-[#e7dcc3] p-2 space-y-1 hidden md:block">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-1 pb-1">Actions</div>
          <FnBtn k="F2" label="Period" onClick={onOpenPeriod} testid="tl-fn-period" />
          <FnBtn k="F5" label="Payment" onClick={() => newVoucher("payment")} testid="tl-fn-payment" />
          <FnBtn k="F6" label="Receipt" onClick={() => newVoucher("receipt")} testid="tl-fn-receipt" />
          {(data?.bills || []).length > 0 && (
            <FnBtn k="F7" label="Bill-wise" onClick={() => setBillsOpen((b) => !b)} testid="tl-fn-bills" />
          )}
          <FnBtn k="Alt+P" label="Export PDF" onClick={() => doExport("pdf")} testid="tl-export-pdf" />
          <FnBtn k="Alt+E" label="Export Excel" onClick={() => doExport("xlsx")} testid="tl-export-excel" />
          <FnBtn k="Alt+D" label="Delete Entry" onClick={() => delRow(rows[sel])} testid="tl-fn-delete" />
          <FnBtn k="Esc" label="Back" onClick={onBack} testid="tl-fn-back" />
        </div>
      </div>

      <KeyBar chips={[["↑↓", "Move"], ["Enter", "Open / Alter"], ["F5", "Payment"], ["F6", "Receipt"],
        ["F7", "Bill-wise"], ["F2", "Period"], ["Alt+P", "PDF"], ["Alt+E", "Excel"], ["Alt+D", "Delete"], ["Esc", "Back"]]} />

      {vform && (
        <VoucherDialog initial={vform} onClose={() => setVform(null)}
          onSaved={() => { setVform(null); load(); }} />
      )}
    </div>
  );
}

/* ---------- Reports overlay (old Display Menu, F9) ---------- */
function ReportsOverlay({ onPick, onClose }) {
  const [sel, setSel] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); e.stopPropagation(); setSel((s) => (s + 1) % REPORT_MENU.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); e.stopPropagation(); setSel((s) => (s - 1 + REPORT_MENU.length) % REPORT_MENU.length); }
      else if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); onPick(REPORT_MENU[sel].key); }
      else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [sel, onPick, onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" data-testid="tl-reports-overlay" onClick={onClose}>
      <div className="bg-[#fbf7ee] border-2 border-[#0f766e] shadow-2xl w-80" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0f766e] text-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase">Reports (F9)</div>
        <div className="py-2">
          {REPORT_MENU.map((m, i) => (
            <button key={m.key} data-testid={`tl-report-${m.key}`}
              onClick={() => onPick(m.key)} onMouseEnter={() => setSel(i)}
              className={`w-full text-left px-4 py-1 text-[13px] font-semibold ${sel === i ? "bg-[#0f766e] text-white" : "text-slate-800 hover:bg-[#f1e9d6]"}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Master: List of Ledgers ---------- */
export default function LedgerViews() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(fyDefault());
  const [master, setMaster] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [openLedger, setOpenLedger] = useState(null);
  const [openFin, setOpenFin] = useState(null);
  const [openCat, setOpenCat] = useState(null);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [openView, setOpenView] = useState(null);
  const searchRef = useRef(null);
  const rowRefs = useRef({});

  const applyPeriod = useCallback((p) => {
    setPeriod(p);
    try { sessionStorage.setItem("tally_period", JSON.stringify(p)); } catch { /* noop */ }
  }, []);

  // Restore drill state from URL — so returning from an entry (invoice/voucher)
  // brings the user back to the SAME screen, one step at a time.
  useEffect(() => {
    const v = searchParams.get("view");
    if (v && VIEW_COMPONENTS[v]) { setOpenView(v); setSearchParams({}, { replace: true }); return; }
    const grp = searchParams.get("grp");
    if (grp) setOpenCat(grp);
    const fin = searchParams.get("fin");
    if (fin && FIN_VIEWS[fin]) { setOpenFin(fin); return; }
    const cat = searchParams.get("cat");
    const name = searchParams.get("name");
    if (cat && (name || searchParams.get("lid"))) {
      setOpenLedger({ cat, name: name || "", id: searchParams.get("lid") || "" });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    axios.get(`${API}/tally/ledgers`, { params: { to: period.to } })
      .then((r) => setMaster(r.data))
      .catch(() => toast.error("Could not load the ledger list"));
  }, [period.to]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => { if (!openLedger && !openFin && !openView) load(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load, openLedger, openFin, openView]);

  const flat = useMemo(() => {
    const items = [];
    const needle = q.trim().toLowerCase();
    if (!openCat) {
      // Tally Gateway: main headings only
      items.push({ type: "grp", key: "fin", label: "Financial Statements", count: FIN_ITEMS.length });
      (master?.categories || []).forEach((cat) => {
        items.push({ type: "grp", key: cat.key, label: cat.label, count: (cat.ledgers || []).length });
      });
      return items;
    }
    if (openCat === "fin") {
      items.push({ type: "cat", label: "Financial Statements", key: "fin" });
      FIN_ITEMS.filter((f) => !needle || f.name.toLowerCase().includes(needle))
        .forEach((f) => items.push({ type: "fin", ...f }));
      return items;
    }
    const cat = (master?.categories || []).find((c) => c.key === openCat);
    if (cat) {
      items.push({ type: "cat", label: cat.label, key: cat.key });
      (cat.ledgers || []).filter((l) => !needle || l.name.toLowerCase().includes(needle))
        .forEach((l) => items.push({ type: "led", cat: cat.key, ...l }));
    }
    return items;
  }, [master, q, openCat]);
  const selectable = useMemo(() => flat.map((it, i) => (it.type !== "cat" ? i : -1)).filter((i) => i >= 0), [flat]);

  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    if (!openLedger && !openFin) setTimeout(() => searchRef.current?.focus(), 50);
  }, [openLedger, openFin, openView]);

  const closeCat = useCallback(() => {
    setOpenCat(null);
    setQ("");
    setSel(0);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const openSel = useCallback((idx) => {
    const fi = selectable[idx];
    if (fi === undefined) return;
    const it = flat[fi];
    if (it.type === "grp") {
      setOpenCat(it.key);
      setQ("");
      setSel(0);
      setSearchParams({ grp: it.key }, { replace: true });
    } else if (it.type === "fin") {
      setOpenFin(it.key);
      setSearchParams({ grp: "fin", fin: it.key }, { replace: true });
    } else {
      setOpenLedger({ cat: it.cat, name: it.name, id: it.id || "" });
      setSearchParams({ grp: it.cat, cat: it.cat, name: it.name, lid: it.id || "" }, { replace: true });
    }
  }, [selectable, flat, setSearchParams]);

  useEffect(() => {
    if (openLedger || openFin || openView || periodOpen || reportsOpen) return undefined;
    const onKey = (e) => {
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      if (e.key === "ArrowDown") { stop(); setSel((s) => Math.min(s + 1, selectable.length - 1)); }
      else if (e.key === "ArrowUp") { stop(); setSel((s) => Math.max(s - 1, 0)); }
      else if (e.key === "PageDown") { stop(); setSel((s) => Math.min(s + 12, selectable.length - 1)); }
      else if (e.key === "PageUp") { stop(); setSel((s) => Math.max(s - 12, 0)); }
      else if (e.key === "Enter") { stop(); openSel(sel); }
      else if (e.key === "Escape") {
        stop();
        if (q) setQ("");
        else if (openCat) closeCat();
        else navigate("/dashboard");
      }
      else if (e.key === "F2") { stop(); setPeriodOpen(true); }
      else if (e.key === "F9") { stop(); setReportsOpen(true); }
      else if (e.key === "F5") { stop(); load(); toast.success("Refreshed"); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [openLedger, openFin, openView, periodOpen, reportsOpen, selectable, sel, q, openCat, closeCat, openSel, navigate, load]);

  useEffect(() => {
    const fi = selectable[sel];
    if (fi !== undefined) rowRefs.current[fi]?.scrollIntoView({ block: "nearest" });
  }, [sel, selectable]);

  if (openView && VIEW_COMPONENTS[openView]) {
    const View = VIEW_COMPONENTS[openView];
    return <View onClose={() => setOpenView(null)} />;
  }

  if (openFin && FIN_VIEWS[openFin]) {
    const FinView = FIN_VIEWS[openFin];
    return (
      <>
        <FinView period={period}
          onBack={() => { setOpenFin(null); setSearchParams(openCat ? { grp: openCat } : {}, { replace: true }); load(); }}
          onOpenPeriod={() => setPeriodOpen(true)}
          blocked={periodOpen} />
        {periodOpen && (
          <PeriodDialog period={period} onClose={() => setPeriodOpen(false)}
            onApply={(p) => { applyPeriod(p); setPeriodOpen(false); }} />
        )}
      </>
    );
  }

  if (openLedger) {
    return (
      <>
        <StatementView ledger={openLedger} period={period}
          onBack={() => { setOpenLedger(null); setSearchParams(openCat ? { grp: openCat } : {}, { replace: true }); load(); }}
          onOpenPeriod={() => setPeriodOpen(true)}
          onOpenReports={() => setReportsOpen(true)}
          blocked={periodOpen || reportsOpen} />
        {periodOpen && (
          <PeriodDialog period={period} onClose={() => setPeriodOpen(false)}
            onApply={(p) => { applyPeriod(p); setPeriodOpen(false); }} />
        )}
        {reportsOpen && (
          <ReportsOverlay onClose={() => setReportsOpen(false)}
            onPick={(k) => { setReportsOpen(false); setOpenView(k); }} />
        )}
      </>
    );
  }

  let selCursor = -1;
  const catLabel = openCat === "fin" ? "Financial Statements"
    : (master?.categories || []).find((c) => c.key === openCat)?.label || "";
  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-[#eef1f4]" data-testid="ledger-views-page">
      <div className="bg-[#0f766e] text-white px-4 py-2 flex items-center justify-between" data-testid="ledger-views-header">
        <div>
          <div className="text-sm font-bold tracking-widest uppercase">
            {openCat ? catLabel : "Ledger Views — Gateway"}
          </div>
          <div className="text-[11px] text-teal-100">
            {openCat ? "Gateway › " + catLabel + " · " : ""}Period: {dshow(period.from)} to {dshow(period.to)}
          </div>
        </div>
        <button onClick={() => setReportsOpen(true)} data-testid="tl-open-reports"
          className="px-3 py-1 text-[11px] font-bold bg-white/15 hover:bg-white/25 rounded-sm">Reports (F9)</button>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col bg-white border-r border-[#e7dcc3]">
          {openCat && (
            <div className="p-2 border-b border-[#eee5cf] bg-[#fbf7ee]">
              <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="tl-search"
                placeholder={`Search in ${catLabel}…  (↑↓ move · Enter open · Esc back)`}
                className="w-full border border-[#d8ccae] rounded-sm px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0f766e]" />
            </div>
          )}
          <div className="flex-1 overflow-y-auto" data-testid="tally-ledger-list">
            {!master && <div className="p-6 text-center text-slate-400 text-sm">Loading ledgers…</div>}
            {flat.map((it, fi) => {
              if (it.type === "cat") {
                return (
                  <div key={`c-${it.key}`} className="px-3 py-1 bg-[#f6efdd] text-[11px] font-bold uppercase tracking-widest text-[#0f766e] border-y border-[#eee5cf] sticky top-0">
                    {it.label}
                  </div>
                );
              }
              selCursor += 1;
              const idx = selCursor;
              const active = selectable[sel] === fi;
              if (it.type === "grp") {
                return (
                  <div key={`g-${it.key}`} ref={(el) => { rowRefs.current[fi] = el; }}
                    data-testid={`tl-grp-${it.key}`}
                    onClick={() => { setSel(idx); openSel(idx); }}
                    onMouseEnter={() => setSel(idx)}
                    className={`flex items-center justify-between px-5 py-3 text-[14px] cursor-pointer border-b border-[#f1ecdd] ${active ? "bg-[#0f766e] text-white" : "hover:bg-[#f6efdd] text-slate-900"}`}>
                    <span className="font-bold tracking-wide">{it.label}</span>
                    <span className={`flex items-center gap-3 text-[12px] ${active ? "text-teal-100" : "text-slate-500"}`}>
                      {it.count ? `${it.count} ${it.key === "fin" ? "reports" : "ledgers"}` : ""}
                      <span className="text-lg leading-none font-bold">›</span>
                    </span>
                  </div>
                );
              }
              return (
                <div key={`l-${it.cat || "fin"}-${it.name}`} ref={(el) => { rowRefs.current[fi] = el; }}
                  data-testid={`tl-row-${fi}`}
                  onClick={() => { setSel(idx); openSel(idx); }}
                  onMouseEnter={() => setSel(idx)}
                  className={`flex items-center justify-between px-4 py-1.5 text-[13px] cursor-pointer border-b border-[#f7f2e4] ${active ? "bg-[#0f766e] text-white" : "hover:bg-[#f6efdd] text-slate-800"}`}>
                  <span className="font-medium truncate">{it.name}</span>
                  <span className={`tabular-nums text-[12px] ${active ? "text-white" : "text-slate-700"}`}>
                    {it.closing > 0.004 ? `${fmt0(it.closing)} ${it.drcr}` : ""}
                  </span>
                </div>
              );
            })}
            {master && openCat && flat.length <= 1 && (
              <div className="p-6 text-center text-slate-400 text-sm">No ledger matches “{q}”</div>
            )}
          </div>
        </div>

        <div className="w-44 bg-[#f6efdd] border-l border-[#e7dcc3] p-2 space-y-1 hidden md:block">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-1 pb-1">Actions</div>
          <FnBtn k="F2" label="Period" onClick={() => setPeriodOpen(true)} testid="tl-master-period" />
          <FnBtn k="F5" label="Refresh" onClick={() => { load(); toast.success("Refreshed"); }} testid="tl-master-refresh" />
          <FnBtn k="F9" label="Reports" onClick={() => setReportsOpen(true)} testid="tl-master-reports" />
          <FnBtn k="Esc" label={openCat ? "Back" : "Quit"} onClick={() => (openCat ? closeCat() : navigate("/dashboard"))} testid="tl-master-quit" />
        </div>
      </div>

      <KeyBar chips={openCat
        ? [["↑↓", "Move"], ["Enter", "Open"], ["Type", "Search"], ["F2", "Period"], ["F9", "Reports"], ["Esc", "Back to Headings"]]
        : [["↑↓", "Move"], ["Enter", "Open Section"], ["F2", "Period"], ["F9", "Reports"], ["F5", "Refresh"], ["Esc", "Quit"]]} />

      {periodOpen && (
        <PeriodDialog period={period} onClose={() => setPeriodOpen(false)}
          onApply={(p) => { applyPeriod(p); setPeriodOpen(false); }} />
      )}
      {reportsOpen && (
        <ReportsOverlay onClose={() => setReportsOpen(false)}
          onPick={(k) => { setReportsOpen(false); setOpenView(k); }} />
      )}
    </div>
  );
}
